import React from 'react';
import { Card, Typography, Tag } from 'antd';
import {
  ISiteVisitQuestion,
  isMapQuestion,
  isPhotosQuestion,
  isFilesQuestion,
  SiteVisitMapType,
} from '../../types';
import { questionCardStyles } from '../../styles/questionCard';
import {
  getQuestionTypeLabel,
  getQuestionTypeColor,
  formatQuestionResult,
  formatDistance,
} from '../../utils';
import MapPointDisplay from './MapPointDisplay';
import PhotosDisplay from './PhotosDisplay';
import FilesDisplay from './FilesDisplay';
import { IMapController } from '../../../../lib/map';

const { Text } = Typography;

interface QuestionCardProps {
  question: ISiteVisitQuestion;
  index: number;
  mapController?: IMapController;
}

const QuestionCard: React.FC<QuestionCardProps> = ({ question, index, mapController }) => {
  const resultText = formatQuestionResult(question);
  const hasResult = question.result && resultText !== 'Not filled';

  const getResultComment = (): string | undefined => {
    if (question.result && 'comment' in question.result) {
      return question.result.comment;
    }
    return undefined;
  };

  const renderResult = () => {
    const comment = getResultComment();

    if (!hasResult) {
      return (
        <div style={questionCardStyles.resultContainer}>
          <Text style={questionCardStyles.resultTextEmpty}>Not filled</Text>
          {comment && <Text style={questionCardStyles.resultComment}>{comment}</Text>}
        </div>
      );
    }

    if (isMapQuestion(question) && question.result?.points) {
      const points = question.result.points;
      const pointColor = question.colors?.point || '#1890ff';

      if (question.mapType === SiteVisitMapType.line && question.result.distance !== undefined) {
        return (
          <div style={questionCardStyles.resultContainer}>
            <Text style={questionCardStyles.resultLabel}>
              Distance: {formatDistance(question.result.distance)}
            </Text>
            <div>
              {points.map((point, pointIndex) => {
                const markerId = `${question.id || index - 1}-${point.id}`;
                return (
                  <MapPointDisplay
                    key={point.id || pointIndex}
                    point={point}
                    index={pointIndex + 1}
                    color={pointColor}
                    markerId={markerId}
                    mapController={mapController}
                  />
                );
              })}
            </div>
            {comment && <Text style={questionCardStyles.resultComment}>{comment}</Text>}
          </div>
        );
      }

      return (
        <div style={questionCardStyles.pointsContainer}>
          <div>
            {points.map((point, pointIndex) => {
              const markerId = `${question.id || index - 1}-${point.id}`;
              return (
                <MapPointDisplay
                  key={point.id || pointIndex}
                  point={point}
                  index={pointIndex + 1}
                  color={pointColor}
                  markerId={markerId}
                  mapController={mapController}
                />
              );
            })}
          </div>
          {comment && <Text style={questionCardStyles.resultComment}>{comment}</Text>}
        </div>
      );
    }

    if (isPhotosQuestion(question) && question.result?.photos) {
      return <PhotosDisplay photos={question.result.photos} comment={question.result.comment} />;
    }

    if (isFilesQuestion(question) && question.result?.files) {
      return <FilesDisplay files={question.result.files} comment={question.result.comment} />;
    }

    return (
      <div style={questionCardStyles.resultContainer}>
        <Text style={questionCardStyles.resultText}>{resultText}</Text>
        {comment && <Text style={questionCardStyles.resultComment}>{comment}</Text>}
      </div>
    );
  };

  return (
    <Card size="small" style={questionCardStyles.card}>
      <div style={questionCardStyles.cardContent}>
        <div style={questionCardStyles.header(!!hasResult)}>
          <Text strong style={questionCardStyles.title}>
            {index}. {question.title}
          </Text>
          <Tag color={getQuestionTypeColor(question.type)} style={questionCardStyles.tag}>
            {getQuestionTypeLabel(question.type)}
          </Tag>
        </div>

        {renderResult()}
      </div>
    </Card>
  );
};

export default QuestionCard;
